import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { PAYMENT_ATTEMPT_STATUSES, PERMISSIONS, type PaymentAttemptStatus } from '@solar/contracts';

import { Alert } from '../../../../components/Alert';
import { Button } from '../../../../components/Button';
import { PageSpinner } from '../../../../components/PageSpinner';
import { StatusBadge } from '../../../../components/StatusBadge';
import { useAuth } from '../../../../features/auth/auth-context';
import { billingApi } from '../../../../features/billing/billing.api';
import { toDisplayMessage } from '../../../../lib/error-message';
import { formatNgn } from '../../../../lib/format-currency';
import { formatStatusLabel } from '../../../../lib/format-status';
import { paths, routeTo } from '../../../../routes/paths';
import styles from '../AdminPortal.module.css';

function formatTimestamp(value: string | null): string {
  return value === null ? '—' : new Date(value).toLocaleString();
}

export function PaymentAttemptsPage(): React.JSX.Element {
  const { id } = useParams<{ id?: string }>();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const [statusFilter, setStatusFilter] = useState<PaymentAttemptStatus | ''>('');

  const attempts = useQuery({
    queryKey: ['admin', 'payment-attempts', statusFilter],
    queryFn: () =>
      billingApi.listAdminPaymentAttempts({
        pageSize: 50,
        ...(statusFilter === '' ? {} : { status: statusFilter }),
      }),
    enabled: id === undefined,
  });

  const attemptDetail = useQuery({
    queryKey: ['admin', 'payment-attempts', id],
    queryFn: () => billingApi.getAdminPaymentAttempt(id ?? ''),
    enabled: id !== undefined,
  });

  const checkStatus = useMutation({
    mutationFn: () => billingApi.checkPaymentAttemptStatus(id ?? ''),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'payment-attempts', id] });
    },
  });

  const canCheckStatus = can(PERMISSIONS.PAYMENTS_RETRY);
  const items = attempts.data?.items ?? [];

  if (id !== undefined) {
    if (attemptDetail.isPending) {
      return <PageSpinner label="Loading payment attempt" />;
    }

    if (attemptDetail.isError) {
      return <Alert tone="error">{toDisplayMessage(attemptDetail.error)}</Alert>;
    }

    const data = attemptDetail.data;

    return (
      <div className={styles.page}>
        <header className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>Payment attempt</h1>
          <StatusBadge status={data.status} />
        </header>

        <p>
          <Link to={paths.adminPaymentAttempts}>Back to payment attempts</Link>
        </p>

        {checkStatus.isError && <Alert tone="error">{toDisplayMessage(checkStatus.error)}</Alert>}
        {checkStatus.isSuccess && <Alert tone="success">Status check completed.</Alert>}

        <section className={styles.card} aria-labelledby="attempt-details-heading">
          <h2 className={styles.cardTitle} id="attempt-details-heading">
            Attempt details
          </h2>

          <dl className={styles.definitionList}>
            <dt>Reference</dt>
            <dd>{data.internalReference}</dd>
            <dt>Customer</dt>
            <dd>
              <Link to={routeTo.adminCustomer(data.customerId)}>{data.customerId.slice(0, 8)}</Link>
            </dd>
            <dt>Schedule</dt>
            <dd>
              <Link to={routeTo.adminBillingRepayment(data.scheduleId)}>
                {data.scheduleId.slice(0, 8)}
              </Link>
            </dd>
            <dt>Contract</dt>
            <dd>{data.subscriptionId.slice(0, 8)}</dd>
            <dt>Attempt #</dt>
            <dd>{data.attemptNumber}</dd>
            <dt>Amount</dt>
            <dd>{formatNgn(data.amount)}</dd>
            <dt>Provider status</dt>
            <dd>{data.providerStatus ?? '—'}</dd>
            <dt>Failure code</dt>
            <dd>{data.failureCode ?? '—'}</dd>
            <dt>Failure message</dt>
            <dd>{data.failureMessage ?? '—'}</dd>
            <dt>Created</dt>
            <dd>{formatTimestamp(data.createdAt)}</dd>
            <dt>Succeeded</dt>
            <dd>{formatTimestamp(data.succeededAt)}</dd>
            <dt>Failed</dt>
            <dd>{formatTimestamp(data.failedAt)}</dd>
          </dl>

          {data.status === 'SUCCEEDED' && (
            <p>
              <Link to={paths.adminPayments}>View posted payments</Link>
            </p>
          )}

          {canCheckStatus &&
            data.status !== 'SUCCEEDED' &&
            data.status !== 'CANCELLED' &&
            data.status !== 'ABANDONED' && (
              <div className={styles.actions}>
                <Button busy={checkStatus.isPending} onClick={() => checkStatus.mutate()}>
                  Check status with provider
                </Button>
              </div>
            )}
        </section>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Payment attempts</h1>
          <p className={styles.pageSubtitle}>Debit attempts across all customers.</p>
        </div>
        <Link to={paths.adminBilling}>Billing dashboard</Link>
      </header>

      <section className={styles.card} aria-labelledby="payment-attempts-heading">
        <h2 className={styles.cardTitle} id="payment-attempts-heading">
          All attempts
        </h2>

        <form className={styles.filters} aria-label="Filter payment attempts">
          <label htmlFor="attemptStatusFilter">
            Status
            <select
              id="attemptStatusFilter"
              name="attemptStatusFilter"
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value as PaymentAttemptStatus | '');
              }}
            >
              <option value="">All statuses</option>
              {PAYMENT_ATTEMPT_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {formatStatusLabel(status)}
                </option>
              ))}
            </select>
          </label>
        </form>

        {attempts.isPending && <PageSpinner label="Loading payment attempts" />}
        {attempts.isError && <Alert tone="error">{toDisplayMessage(attempts.error)}</Alert>}

        {attempts.isSuccess && items.length === 0 && (
          <p className={styles.empty}>
            No payment attempts match these filters. Failed and processing attempts will appear here
            when they need review.
          </p>
        )}

        {attempts.isSuccess && items.length > 0 && (
          <div className={styles.tableWrapper}>
            <table className={styles.table} aria-label="Payment attempts">
              <thead>
                <tr>
                  <th scope="col">Reference</th>
                  <th scope="col">Customer</th>
                  <th scope="col">Amount</th>
                  <th scope="col">Status</th>
                  <th scope="col">Provider</th>
                  <th scope="col">Created</th>
                </tr>
              </thead>
              <tbody>
                {items.map((attempt) => (
                  <tr key={attempt.id}>
                    <td>
                      <Link to={routeTo.adminPaymentAttempt(attempt.id)}>
                        {attempt.internalReference}
                      </Link>
                    </td>
                    <td>{attempt.customerId.slice(0, 8)}</td>
                    <td>{formatNgn(attempt.amount)}</td>
                    <td>
                      <StatusBadge status={attempt.status} />
                    </td>
                    <td>{attempt.providerStatus ?? '—'}</td>
                    <td>{formatTimestamp(attempt.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
